import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import {
  CARD_ELEMENT_TYPE,
  PHOTO_TYPE,
} from '../../../../../services/cards/constants';
import {
  getCardTemplateSizing,
  isCardElementEditable,
} from '../../../../../services/cards/components/AccountCard/helpers';
import CardDesignerCardElementFrame from '../CardDesignerCardElements/CardDesignerCardElementFrame';
import CardDesignerCardElementPhoto from '../CardDesignerCardElements/CardDesignerCardElementPhoto';
import CardDesignerCardElementText from '../CardDesignerCardElements/CardDesignerCardElementText';
import CardDesignerCardElementSticker from '../CardDesignerCardElements/CardDesignerCardElementSticker/CardDesignerCardElementSticker';
import CardDesignerCardElementRectangle from '../CardDesignerCardElements/CardDesignerCardElementRectangle/CardDesignerCardElementRectangle';
import { isElementCardBackground } from '../CardDesignerControlPanel/components/CardDesignerBackgroundControls/helpers';
import { isElementCardForeground } from '../CardDesignerControlPanel/components/CardDesignerForegroundControls/helpers';
import CardDesignerCardElementBackground from '../CardDesignerCardElements/CardDesignerCardElementBackground';
import CardDesignerCardElementForeground from '../CardDesignerCardElements/CardDesignerCardElementForeground';
import CardDesignerCardElementBarcode from '../CardDesignerCardElements/CardDesignerCardElementBarcode';
import useComponentBounds from '../../../../../hooks/useComponentBounds';
import { DESIGN_MODES } from '../../constants';

const propTypes = {
  className: PropTypes.string,
  cardRef: PropTypes.shape({
    current: PropTypes.object,
  }),
  cardTemplate: PropTypes.shape({
    id: PropTypes.string.isRequired,
    print_product: PropTypes.shape({
      id: PropTypes.string.isRequired,
      print_size: PropTypes.shape({
        id: PropTypes.string.isRequired,
        width: PropTypes.number.isRequired,
        height: PropTypes.number.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
  elements: PropTypes.arrayOf(
    PropTypes.shape({
      cardElement: PropTypes.object.isRequired,
      cardElementConfiguration: PropTypes.object.isRequired,
    }).isRequired,
  ).isRequired,
  selectedCardElementId: PropTypes.string,
  onCardElementSelect: PropTypes.func.isRequired,
  onCardElementUpdate: PropTypes.func.isRequired,
  onCardElementDelete: PropTypes.func,
  onElementConfigurationChange: PropTypes.func.isRequired,
  designMode: PropTypes.oneOf(Object.values(DESIGN_MODES)),
};

const CardDesignerCardSide = ({
  className,
  cardRef,
  cardTemplate,
  elements,
  selectedCardElementId,
  onCardElementSelect,
  onCardElementUpdate,
  onCardElementDelete,
  onElementConfigurationChange,
  designMode,
}) => {
  const classes = classNames({
    'card-designer-card-side': true,
    [className]: !!className,
  });

  const { bounds } = useComponentBounds(cardRef);
  const printSize =
    cardTemplate.print_size ?? cardTemplate.print_product?.print_size; // For back-compat reasons
  const {
    ratio,
    width: cardWidth,
    height: cardHeight,
  } = getCardTemplateSizing({
    orientation: cardTemplate.orientation,
    print_size: {
      width: printSize?.width,
      height: printSize?.height,
    },
  });

  const editablePhotoElements = useMemo(
    () =>
      (elements ?? []).filter(
        ({ cardElement, cardElementConfiguration }) =>
          cardElement.type === CARD_ELEMENT_TYPE.PHOTO &&
          [undefined, PHOTO_TYPE.PHOTO].includes(
            cardElementConfiguration?.photo_type,
          ) &&
          isCardElementEditable({ cardElementConfiguration }),
      ),
    [elements],
  );

  return (
    <div
      role="button"
      tabIndex={0}
      className={classes}
      onClick={({ target }) => {
        if (target?.className === 'card-designer-card-side') {
          onCardElementSelect(undefined);
        }
      }}
      style={{
        paddingBottom: `${ratio * 100}%`,
      }}
    >
      {elements.map(element => {
        const { cardElement } = element;
        const isSelected = selectedCardElementId === cardElement.id;
        const isEditable = true;
        const isPortraitSlot =
          designMode === DESIGN_MODES.ID_CARDS &&
          editablePhotoElements.length === 1 &&
          editablePhotoElements[0].cardElement.id === cardElement.id;

        console.log(elements, isPortraitSlot, editablePhotoElements);
        // For rendering purposes, assume any photo element with no
        // edit capabilities & a full frame is considered a background element
        if (isElementCardForeground(element)) {
          return (
            <CardDesignerCardElementForeground
              key={cardElement.id}
              element={element}
            />
          );
        }

        if (isElementCardBackground(element, true)) {
          return (
            <CardDesignerCardElementBackground
              key={cardElement.id}
              element={element}
            />
          );
        }

        return (
          <CardDesignerCardElementFrame
            key={cardElement.id}
            element={element}
            cardBounds={bounds}
            cardPrintSize={printSize}
            cardOrientation={cardTemplate.orientation}
            onCardElementUpdate={onCardElementUpdate}
            onCardElementDelete={onCardElementDelete}
            onCardElementSelect={onCardElementSelect}
            isSelected={isSelected}
            resizable={isEditable}
            draggable={isEditable}
            rotable={isEditable}
          >
            {cardElement.type === CARD_ELEMENT_TYPE.PHOTO && (
              <CardDesignerCardElementPhoto
                element={element}
                cardBounds={{ width: cardWidth, height: cardHeight }}
                helperText={
                  isPortraitSlot ? (
                    <div className="text-center">
                      Student headshot will automatically be inserted here
                    </div>
                  ) : undefined
                }
                onClick={() => {
                  if (isEditable) {
                    onCardElementSelect(cardElement.id);
                  }
                }}
              />
            )}

            {cardElement.type === CARD_ELEMENT_TYPE.TEXT && (
              <CardDesignerCardElementText
                element={element}
                onClick={() => {
                  if (isEditable) {
                    onCardElementSelect(cardElement.id);
                  }
                }}
                onElementConfigurationChange={onElementConfigurationChange}
              />
            )}

            {cardElement.type === CARD_ELEMENT_TYPE.STICKER && (
              <CardDesignerCardElementSticker
                element={element}
                onClick={() => {
                  if (isEditable) {
                    onCardElementSelect(cardElement.id);
                  }
                }}
              />
            )}

            {cardElement.type === CARD_ELEMENT_TYPE.RECTANGLE && (
              <CardDesignerCardElementRectangle
                element={element}
                onClick={() => {
                  if (isEditable) {
                    onCardElementSelect(cardElement.id);
                  }
                }}
              />
            )}
            {cardElement.type === CARD_ELEMENT_TYPE.BARCODE && (
              <CardDesignerCardElementBarcode
                element={element}
                onClick={() => {
                  if (isEditable) {
                    onCardElementSelect(cardElement.id);
                  }
                }}
              />
            )}
          </CardDesignerCardElementFrame>
        );
      })}
    </div>
  );
};

CardDesignerCardSide.propTypes = propTypes;

export default CardDesignerCardSide;
